import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, SectionCard, Loading, EmptyState, Money } from "@/components/common/ui";
import { KpiTile } from "@/components/KpiTile";
import { PaymentStatusBar } from "@/components/PaymentStatusBar";
import { normPayStatus } from "@/lib/payments";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { C, axisProps } from "@/lib/chart";
import { axisINR, formatDate, formatCount } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { FileText, Wallet, AlertTriangle, Clock, CheckCircle2, Paperclip, Eye, Search, Building2, Hash, Download } from "lucide-react";

export const PAYMENT_STATUS_META = {
  Paid: { color: "hsl(var(--success))", chip: "bg-[hsl(var(--success)/0.14)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.3)]" },
  "Paid in Full": { color: "hsl(var(--success))", chip: "bg-[hsl(var(--success)/0.14)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.3)]" },
  "Partially Paid": { color: "hsl(var(--info))", chip: "bg-[hsl(var(--info)/0.14)] text-[hsl(var(--info))] border-[hsl(var(--info)/0.3)]" },
  Pending: { color: "hsl(var(--warning))", chip: "bg-[hsl(var(--warning)/0.16)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.32)]" },
  "Not Paid": { color: "hsl(var(--warning))", chip: "bg-[hsl(var(--warning)/0.16)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.32)]" },
  Overdue: { color: "hsl(var(--danger))", chip: "bg-[hsl(var(--danger)/0.14)] text-[hsl(var(--danger))] border-[hsl(var(--danger)/0.3)]" },
  "PO Not Found": { color: "hsl(var(--danger))", chip: "bg-[hsl(var(--danger)/0.14)] text-[hsl(var(--danger))] border-[hsl(var(--danger)/0.3)]" },
  "No PO Reference": { color: "hsl(var(--chart-cat-7))", chip: "bg-[hsl(var(--surface-3))] text-muted-foreground border-[hsl(var(--border))]" },
  "Non-PO Reference": { color: "hsl(var(--chart-cat-7))", chip: "bg-[hsl(var(--surface-3))] text-muted-foreground border-[hsl(var(--border))]" },
  Unspecified: { color: "hsl(var(--chart-cat-7))", chip: "bg-[hsl(var(--surface-3))] text-muted-foreground border-[hsl(var(--border))]" },
};
const STATUS_OPTIONS = ["Unspecified", "Pending", "Partially Paid", "Paid", "Overdue"];

export function PaymentBadge({ status }) {
  const meta = PAYMENT_STATUS_META[status] || PAYMENT_STATUS_META.Unspecified;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap", meta.chip)}
      data-testid={`payment-status-${status}`}>
      {(status === "Paid" || status === "Paid in Full") && <CheckCircle2 className="h-3 w-3" />}
      {(status === "Overdue" || status === "PO Not Found") && <AlertTriangle className="h-3 w-3" />}
      {(status === "Pending" || status === "Not Paid") && <Clock className="h-3 w-3" />}
      {status}
    </span>
  );
}

const PERIODS = ["FY", "Q1", "Q2", "Q3", "Q4"];

export default function Invoices() {
  const { money } = useCurrency();
  const { isManager } = useAuth();
  const [period, setPeriod] = useState("FY");
  const [statusFilter, setStatusFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [onlyAttached, setOnlyAttached] = useState(false);
  const [report, setReport] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // spend row being edited

  const load = () => {
    setLoading(true);
    // Vertical leads only ever see the invoices they logged themselves.
    const mine = isManager ? { mine: true } : {};
    const params = period === "FY" ? { ...mine } : { quarter: period, ...mine };
    Promise.all([
      api.invoicesReport({ period, ...mine }),
      api.listSpends(params),
    ]).then(([rep, sp]) => {
      setReport(rep);
      setRows(sp);
      setLoading(false);
    }).catch(() => { setLoading(false); toast.error("Could not load invoices"); });
  };
  useEffect(load, [period, isManager]);

  const projectOptions = useMemo(() => Array.from(new Set(rows.map((r) => r.project).filter(Boolean))).sort(), [rows]);

  // rows after every filter EXCEPT payment status (used for the status totals bar)
  const preStatus = useMemo(() => {
    let r = rows;
    if (projectFilter !== "all") r = r.filter((x) => x.project === projectFilter);
    if (onlyAttached) r = r.filter((x) => x.invoice_file_id);
    const q = search.trim().toLowerCase();
    if (q) r = r.filter((x) => [x.vendor, x.invoice_no, x.project, x.billing_business, x.vertical, x.logged_by, x.po_number, x.transaction_id]
      .some((f) => (f || "").toLowerCase().includes(q)));
    return r;
  }, [rows, projectFilter, onlyAttached, search]);

  const filtered = useMemo(
    () => (statusFilter === "all" ? preStatus : preStatus.filter((x) => normPayStatus(x.payment_status) === statusFilter)),
    [preStatus, statusFilter]
  );

  const attachedCount = useMemo(() => rows.filter((r) => r.invoice_file_id).length, [rows]);

  if (loading && !report) return <Loading />;
  if (!report) return <EmptyState title="No data" message="Could not load invoices." />;

  const t = report.totals;
  const statusBars = report.by_status.map((s) => ({ ...s, fill: (PAYMENT_STATUS_META[s.status] || {}).color }));

  return (
    <div className="space-y-5" data-testid="invoices-page">
      <PageHeader title="Invoice & Payments" description={isManager ? "The invoices you have logged — track pending, partially-paid, paid and overdue payments for your verticals." : "Every bill in one place — track pending, partially-paid, paid and overdue payments across all verticals."}>
        <Button variant="outline" onClick={() => {
          const params = { period };
          if (isManager) params.mine = true;
          if (projectFilter !== "all") params.project = projectFilter;
          if (search.trim()) params.search = search.trim();
          if (onlyAttached) params.attached_only = true;
          api.downloadExport("/reports/invoices/export", params, "invoices_export.xlsx")
            .then(() => toast.success("Excel downloaded"))
            .catch(() => toast.error("Export failed"));
        }} data-testid="invoices-export-button"><Download className="mr-1.5 h-4 w-4" /> Export Excel</Button>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[130px]" data-testid="invoice-period-selector"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map((p) => <SelectItem key={p} value={p} data-testid={`invoice-period-${p}`}>{p === "FY" ? "Full Year" : p}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
        <KpiTile testId="kpi-inv-total" title="Total Billed" accent="hsl(var(--chart-cat-7))" icon={FileText} value={<Money value={t.total} />} sub={`${formatCount(t.count)} invoices`} />
        <KpiTile testId="kpi-inv-paid" title="Paid" accent="hsl(var(--success))" icon={CheckCircle2} value={<Money value={t.paid} />} sub={t.total ? `${((t.paid / t.total) * 100).toFixed(1)}% cleared` : "—"} />
        <KpiTile testId="kpi-inv-outstanding" title="Outstanding" accent="hsl(var(--chart-cat-2))" icon={Wallet} value={<Money value={t.outstanding} />} sub="Yet to be paid" />
        <KpiTile testId="kpi-inv-pending" title="Pending" accent="hsl(var(--warning))" icon={Clock} value={<Money value={t.pending} />} sub="Awaiting processing" />
        <KpiTile testId="kpi-inv-overdue" title="Overdue" accent="hsl(var(--danger))" icon={AlertTriangle} value={<Money value={t.overdue} />} sub="Needs attention" />
      </div>

      {/* charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SectionCard title="Amount by Payment Status" description="Where every rupee sits in the payment cycle." testId="card-inv-status">
          <div className="px-2 py-4" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusBars} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.grid} horizontal={false} />
                <XAxis type="number" tickFormatter={axisINR} {...axisProps} />
                <YAxis type="category" dataKey="status" width={104} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }} />
                <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} cursor={{ fill: "hsl(var(--surface-2))" }} />
                <Bar dataKey="amount" name="Amount" radius={[0, 4, 4, 0]} barSize={16} isAnimationActive={false}>
                  {statusBars.map((s) => <Cell key={s.status} fill={s.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Top Vendors by Spend" description="Largest payees this period." testId="card-inv-vendors">
          <div className="table-scroll" style={{ maxHeight: 280 }}>
            <table className="w-full border-collapse">
              <thead><tr>
                <th className="th-sticky th-sticky-left px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Vendor</th>
                <th className="th-sticky px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Spend</th>
              </tr></thead>
              <tbody>
                {report.by_vendor.length === 0 ? (
                  <tr><td colSpan={2} className="px-3 py-8 text-center text-sm text-muted-foreground">No vendor spend.</td></tr>
                ) : report.by_vendor.map((v, i) => (
                  <tr key={v.vendor} className={cn("border-b last:border-0", i % 2 && "row-zebra")}>
                    <td className="td-sticky-left px-3 py-2 text-sm">{v.vendor}</td>
                    <td className="px-3 py-2 num text-sm"><Money value={v.spend} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      {/* Invoice ledger */}
      <SectionCard
        title="Invoice Ledger"
        description={`${filtered.length} of ${rows.length} invoice(s) · ${attachedCount} with an attached file — click "Update" to record a payment.`}
        testId="card-inv-ledger"
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vendor, invoice, business…"
                className="h-9 w-[220px] pl-8" data-testid="invoice-search" />
            </div>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="invoice-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projectOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant={onlyAttached ? "default" : "outline"} size="sm" className="h-9 gap-1.5" onClick={() => setOnlyAttached((v) => !v)} data-testid="invoice-attached-toggle">
              <Paperclip className="h-3.5 w-3.5" /> With file
            </Button>
          </div>
        }
      >
        <div className="border-b p-4">
          <PaymentStatusBar rows={preStatus} value={statusFilter} onChange={setStatusFilter} testId="invoice-pay-bar" />
        </div>
        <div className="table-scroll" style={{ maxHeight: 560 }}>
          <table className="w-full border-collapse">
            <thead><tr>
              {["Invoice / Date", "Vendor / Business", "PO / Txn", "Project", "Vertical", "Amount", "Status", "File", ""].map((h, i) => (
                <th key={h + i} className={cn("th-sticky px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                  i === 0 ? "th-sticky-left text-left" : i === 5 ? "text-right" : i === 7 ? "text-center" : "text-left")}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-12 text-center text-sm text-muted-foreground">No invoices match this filter.</td></tr>
              ) : filtered.map((r, i) => (
                <tr key={r.id} data-testid={`invoice-row-${i}`} className={cn("border-b last:border-0 hover:bg-[hsl(var(--primary)/0.05)]", i % 2 && "row-zebra")}>
                  <td className="td-sticky-left px-3 py-2.5">
                    <p className="text-xs font-medium">{r.invoice_no || r.proforma_text || "—"}</p>
                    <p className="text-[11px] text-muted-foreground">{formatDate(r.invoice_date || r.date)}{r.logged_by ? ` · by ${r.logged_by}` : ""}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="text-sm">{r.vendor}</p>
                    {r.billing_business && <p className="flex items-center gap-1 text-[11px] text-muted-foreground"><Building2 className="h-3 w-3" />{r.billing_business}</p>}
                  </td>
                  <td className="px-3 py-2.5" data-testid={`invoice-po-cell-${i}`}>
                    {r.po_number ? (
                      <p className="flex items-center gap-1 font-mono text-xs"><Hash className="h-3 w-3 text-muted-foreground" />{r.po_number}</p>
                    ) : <p className="text-[11px] text-muted-foreground">No PO</p>}
                    {r.transaction_id && <p className="font-mono text-[10px] text-muted-foreground" title="Transaction ID">txn {r.transaction_id}</p>}
                    {r.payment_date && <p className="text-[10px] text-muted-foreground" title="Payment date">paid {formatDate(r.payment_date)}</p>}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-muted-foreground">{r.project}</td>
                  <td className="px-3 py-2.5 text-xs"><span className="rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-medium">{r.vertical}</span></td>
                  <td className="px-3 py-2.5 num text-sm font-medium"><Money value={r.amount} /></td>
                  <td className="px-3 py-2.5"><PaymentBadge status={r.payment_status || "Unspecified"} /></td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      {r.invoice_file_id ? (
                        <a href={api.fileUrl(r.invoice_file_id)} target="_blank" rel="noreferrer" title={r.invoice_file_name || "View invoice"}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.1)]" data-testid={`invoice-file-view-${i}`}>
                          <Eye className="h-3.5 w-3.5" /> View
                        </a>
                      ) : <span className="text-[11px] text-muted-foreground">—</span>}
                      <button type="button" title={r.invoice_file_id ? "Download invoice copy" : "No digital copy available"}
                        onClick={() => {
                          if (!r.invoice_file_id) { toast.message("No digital copy available for this invoice"); return; }
                          api.downloadFile(r.invoice_file_id, r.invoice_file_name)
                            .then(() => toast.success("Invoice downloaded"))
                            .catch(() => toast.error("Download failed"));
                        }}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-[hsl(var(--primary)/0.1)] hover:text-[hsl(var(--primary))]"
                        data-testid={`invoice-file-download-${i}`}>
                        <Download className="h-3.5 w-3.5" /> Download
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" data-testid={`invoice-update-${i}`} onClick={() => setEditing(r)}>Update</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <PaymentDialog row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}

function PaymentDialog({ row, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (row) setForm({
      payment_status: row.payment_status || "Unspecified",
      invoice_no: row.invoice_no || "",
      due_date: row.due_date || "",
      paid_date: row.paid_date || "",
      amount_paid: row.amount_paid || 0,
      notes: row.notes || "",
    });
  }, [row]);
  if (!row || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dialogStatusOptions = Array.from(new Set([row.payment_status || "Unspecified", ...STATUS_OPTIONS]));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, amount_paid: Number(form.amount_paid) || 0 };
      if (body.payment_status === "Paid" || body.payment_status === "Paid in Full") body.amount_paid = row.amount;
      await api.updatePayment(row.id, body);
      toast.success("Payment updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update payment");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>{row.vendor} · {row.project} · <Money value={row.amount} /></DialogDescription>
        </DialogHeader>
        {row.invoice_file_id && (
          <a href={api.fileUrl(row.invoice_file_id)} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 rounded-md border bg-secondary/50 px-3 py-2 text-xs text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.1)]" data-testid="payment-invoice-view">
            <Paperclip className="h-3.5 w-3.5" /> View attached invoice{row.invoice_file_name ? ` — ${row.invoice_file_name}` : ""}
          </a>
        )}
        {(row.po_number || row.transaction_id || row.payment_date || row.payment_amount || row.balance_payable) && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-md border bg-[hsl(var(--surface-2))] px-3 py-2 text-xs" data-testid="payment-po-details">
            {row.po_number && <div><span className="text-muted-foreground">PO Number</span><p className="font-mono font-medium">{row.po_number}</p></div>}
            {row.transaction_id && <div><span className="text-muted-foreground">Transaction ID</span><p className="font-mono font-medium">{row.transaction_id}</p></div>}
            {row.payment_date && <div><span className="text-muted-foreground">Payment Date</span><p className="font-medium">{formatDate(row.payment_date)}</p></div>}
            {row.payment_amount != null && row.payment_amount !== 0 && <div><span className="text-muted-foreground">Payment Amount</span><p className="font-medium tabular-nums"><Money value={row.payment_amount} /></p></div>}
            {row.balance_payable != null && row.balance_payable !== 0 && <div><span className="text-muted-foreground">Balance Payable</span><p className="font-medium tabular-nums"><Money value={row.balance_payable} /></p></div>}
            {row.matched_po_value != null && row.matched_po_value !== 0 && <div><span className="text-muted-foreground">Matched PO Value</span><p className="font-medium tabular-nums"><Money value={row.matched_po_value} /></p></div>}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Payment Status</Label>
            <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)}>
              <SelectTrigger className="mt-1" data-testid="payment-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{dialogStatusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Invoice No.</Label>
              <Input className="mt-1" value={form.invoice_no} onChange={(e) => set("invoice_no", e.target.value)} data-testid="payment-invoice-no" />
            </div>
            <div>
              <Label className="text-xs">Amount Paid (₹)</Label>
              <Input className="mt-1" type="number" value={form.amount_paid} onChange={(e) => set("amount_paid", e.target.value)} data-testid="payment-amount-paid"
                disabled={form.payment_status !== "Partially Paid"} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Due Date</Label>
              <Input className="mt-1" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="payment-due-date" />
            </div>
            <div>
              <Label className="text-xs">Paid Date</Label>
              <Input className="mt-1" type="date" value={form.paid_date} onChange={(e) => set("paid_date", e.target.value)} data-testid="payment-paid-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea className="mt-1" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="payment-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="payment-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="payment-save">{saving ? "Saving…" : "Save Payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
